import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

import { DEFAULT_MODAL_HEADER_HEIGHT } from '@/consts/modal';
import { buildTranslateStyle, calculateConstrainedPosition, INITIAL_TRANSFORM } from '@/utils/draggable';

interface UseDraggableOptions {
  enabled?: boolean;
  headerRef?: RefObject<HTMLElement | null>;
  isOpen?: boolean;
  wrapperRef?: RefObject<HTMLElement | null>;
}

export function useDraggable(
  contentRef: RefObject<HTMLElement | null>,
  { enabled = true, headerRef, isOpen = false, wrapperRef }: UseDraggableOptions = {}
) {
  const isDraggingRef = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const currentOffset = useRef({ x: 0, y: 0 });
  const cachedDimensions = useRef({ modalHeight: 0, headerHeight: 0 });

  useEffect(() => {
    if (isOpen && enabled) {
      currentOffset.current = { x: 0, y: 0 };
      const wrapper = wrapperRef?.current;
      if (wrapper) {
        wrapper.style.transform = INITIAL_TRANSFORM;
      }
    }
  }, [isOpen, enabled, wrapperRef]);

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!isDraggingRef.current || !enabled) return;

      const wrapper = wrapperRef?.current;
      if (!wrapper) return;

      const newX = e.clientX - dragStartPos.current.x;
      const newY = e.clientY - dragStartPos.current.y;

      const { x: constrainedX, y: constrainedY } = calculateConstrainedPosition(newX, newY, {
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        ...cachedDimensions.current
      });

      currentOffset.current = { x: constrainedX, y: constrainedY };
      wrapper.style.transform = buildTranslateStyle(constrainedX, constrainedY);
    },
    [enabled, wrapperRef]
  );

  const handlePointerUp = useCallback(() => {
    isDraggingRef.current = false;
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
  }, [handlePointerMove]);

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (!enabled || !contentRef.current) return;

      const target = e.target as HTMLElement;
      if (target.closest('[data-slot="dialog-close"]') || target.closest('button')) return;

      isDraggingRef.current = true;
      dragStartPos.current = {
        x: e.clientX - currentOffset.current.x,
        y: e.clientY - currentOffset.current.y
      };

      cachedDimensions.current = {
        modalHeight: contentRef.current.offsetHeight,
        headerHeight: headerRef?.current?.offsetHeight ?? DEFAULT_MODAL_HEADER_HEIGHT
      };

      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);

      e.preventDefault();
    },
    [enabled, contentRef, headerRef, handlePointerMove, handlePointerUp]
  );

  return {
    handlePointerDown
  };
}
